import { string } from "prop-types";
import { emptyApi } from "./emptyApi";
import { FileType } from "components/FileInput/FileInput";

export type ParsedShareholder = {
  name: string;
  percentage: string;
};

export type Parsed1120FieldType = {
  key: string;
  value: ParsedShareholder[] | string | number;
  label: string;
  tooltip?: string;
  error_message?: string;
  warning_message?: string;
  is_edited: boolean;
};

export type FilingDraftResponse = {
  uuid: string;
  group_id: string;
  message_id: string;
  submitted_at: string | null;
  submitted_by: string | null;
  status: "REQUESTED" | "APPROVED" | "REJECTED";
  parsed_fields: Parsed1120FieldType[];
  is_approved: boolean | null;
  rejection_reason: string | null;
  file_id: string | null;
};

export type Parsed1120Data = {
  all_fields: Record<string, string>;
  fields: Parsed1120FieldType[];
  file: { download_url: string; preview_url: string; uuid: string } | null;
};

export const actionItemsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getFilingDraftDetails: build.query<
      FilingDraftResponse,
      { groupId: string; draftId: string }
    >({
      query: ({ groupId, draftId }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/review-draft/?review_draft_id=${draftId}`,
        };
      },
      providesTags: () => [{ type: "FILING_DRAFT_CARD_DETAILS" }],
    }),
    parse1120Document: build.mutation<
      Parsed1120Data,
      {
        file?: File | null;
        fileId?: string;
        groupId: string;
      }
    >({
      query: ({ file, fileId, groupId }) => {
        const formData = new FormData();
        if (file) {
          formData.append("files", file);
        }
        if (fileId) {
          formData.append("file_id", fileId);
        }
        formData.append("group_id", groupId);

        return {
          url: `/api/inkle/documents/parse/1120/`,
          method: "post",
          body: formData,
        };
      },
    }),
    submitFilingDraftCard: build.mutation<
      Parsed1120FieldType[],
      {
        groupId: string;
        draftId: string;
        isApproved: boolean;
        rejectionReason?: string | null;
      }
    >({
      query: ({ groupId, draftId, isApproved, rejectionReason }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/dft-review-draft/${draftId}/submit/`,
          method: "post",
          body: {
            is_approved: isApproved,
            rejection_reason: rejectionReason,
          },
        };
      },
      invalidatesTags: ["ALL_ACTION_ITEMS", "Tasks"],
    }),
    createFilingDraft: build.mutation<
      Parsed1120FieldType[],
      {
        groupId: string;
        channel_id: string;
        request_data: Record<string, any>;
        message: string;
        editedFields: string[];
        deletedFields: string[];
      }
    >({
      query: ({
        channel_id,
        request_data,
        groupId,
        message,
        editedFields,
        deletedFields,
      }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/review-draft/`,
          method: "post",
          body: {
            channel_id,
            request_data: {
              ...request_data,
              edited_fields: editedFields,
              deleted_fields: deletedFields,
            },
            message_text: message,
          },
        };
      },
    }),
    updateDraftValue: build.mutation<
      Parsed1120Data,
      {
        documentType: string;
        data: Record<string, any>;
      }
    >({
      query: ({ data, documentType }) => {
        return {
          url: `/api/inkle/documents/validate-fields/`,
          method: "post",
          body: {
            data,
            document_type: documentType,
          },
        };
      },
    }),

    createDftDraft: build.mutation<
      Parsed1120FieldType[],
      {
        groupId: string;
        channelId: string;
        requestData: {
          file_id: string;
          tax_amount_payable: number;
          gross_asset_value: number;
        };
        message: string;
      }
    >({
      query: ({ channelId, requestData, groupId, message }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/dft-review-draft/`,
          method: "post",
          body: {
            channel_id: channelId,
            request_data: requestData,
            message_text: message,
          },
        };
      },
      invalidatesTags: ["ALL_ACTION_ITEMS", "Tasks"],
    }),
  }),
});

export const {
  useParse1120DocumentMutation,
  useSubmitFilingDraftCardMutation,
  useCreateFilingDraftMutation,
  useGetFilingDraftDetailsQuery,
  useUpdateDraftValueMutation,
  useCreateDftDraftMutation,
} = actionItemsApi;
